import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Button,
  Divider,
  IconButton,
  InputBase,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import SearchIcon from '@material-ui/icons/Search';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import {
  ProfileInfo,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import LogoFull from './LogoFull';
import { trackUxSignal } from '../ui';

/** Height of the RHDH-style global header (PatternFly masthead). */
export const GLOBAL_HEADER_HEIGHT = 64;

/*
 * Global header modeled on Red Hat Developer Hub's global header plugin:
 * dark masthead (in both themes), brand on the left, global search in the
 * middle, and Self-service / Help / Profile actions on the right.
 */
const useStyles = makeStyles(theme => ({
  appbar: {
    backgroundColor: '#212427',
    // Inset hairline instead of border-bottom so the header stays exactly
    // GLOBAL_HEADER_HEIGHT tall (a border would add 1px to the box).
    boxShadow: 'inset 0 -1px 0 rgba(255, 255, 255, 0.12)',
    // In-flow header inside the shell flex column — never overlaps.
    flexShrink: 0,
  },
  toolbar: {
    minHeight: GLOBAL_HEADER_HEIGHT,
    gap: 16,
    paddingLeft: 16,
    paddingRight: 16,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    flexShrink: 0,
  },
  search: {
    flex: 1,
    maxWidth: 720,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '0 auto',
    padding: '4px 12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
    transition: 'border-color 150ms ease',
    '&:focus-within': {
      borderBottomColor: '#1FA7F8',
    },
  },
  searchIcon: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 20,
  },
  searchInput: {
    color: '#ffffff',
    width: '100%',
    fontSize: '0.9rem',
    '& input::placeholder': {
      color: 'rgba(255, 255, 255, 0.6)',
      opacity: 1,
    },
  },
  action: {
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  iconButton: {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  verticalDivider: {
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
    height: 24,
    alignSelf: 'center',
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: '0.8rem',
    backgroundColor: '#0066CC',
  },
  userName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

export const GlobalHeader = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);

  const [profile, setProfile] = React.useState<ProfileInfo | undefined>();
  const [userRef, setUserRef] = React.useState<string | undefined>();
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(setProfile)
      .catch(() => {});
    identityApi
      .getBackstageIdentity()
      .then(identity => setUserRef(identity.userEntityRef))
      .catch(() => {});
  }, [identityApi]);

  const onSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && query.trim()) {
      trackUxSignal('ui.header.search.submitted', {});
      navigate(`/search?query=${encodeURIComponent(query.trim())}`);
    }
  };

  const openProfileEntity = () => {
    setMenuAnchor(null);
    // userEntityRef looks like `user:default/jdoe`
    const match = userRef?.match(/^user:([^/]+)\/(.+)$/);
    if (match) {
      navigate(`/catalog/${match[1]}/user/${match[2]}`);
    }
  };

  const signOut = async () => {
    setMenuAnchor(null);
    trackUxSignal('ui.header.signOut', {});
    await identityApi.signOut();
  };

  const displayName = profile?.displayName ?? 'Profile';
  const initials = displayName
    .split(' ')
    .map(part => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <AppBar position="static" className={classes.appbar}>
      <Toolbar className={classes.toolbar}>
        <Link to="/" className={classes.brand} aria-label="Home">
          <LogoFull />
        </Link>

        <div className={classes.search}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder="Search..."
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            inputProps={{ 'aria-label': 'Search' }}
          />
        </div>

        <Button
          className={classes.action}
          startIcon={<AddIcon />}
          onClick={() => navigate('/create')}
        >
          Self-service
        </Button>

        <IconButton
          className={classes.iconButton}
          size="small"
          aria-label="Help"
          onClick={() => navigate('/docs')}
        >
          <HelpOutlineIcon />
        </IconButton>

        <Divider orientation="vertical" className={classes.verticalDivider} />

        <Button
          className={classes.action}
          onClick={event => setMenuAnchor(event.currentTarget)}
          aria-label="Profile menu"
          endIcon={<ExpandMoreIcon />}
        >
          <Avatar src={profile?.picture} className={classes.avatar}>
            {initials}
          </Avatar>
          <Typography component="span" className={classes.userName}>
            {displayName}
          </Typography>
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={openProfileEntity} disabled={!userRef}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            My profile
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              navigate('/settings');
            }}
          >
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={signOut}>
            <ListItemIcon>
              <PowerSettingsNewIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
